import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Chip,
  Avatar,
  Select,
  MenuItem,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Modal,
  IconButton,
  Divider,
  // List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Search,
  Briefcase,
  GraduationCap,
  Calendar,
  Check,
  Eye,
  X,
  AlertTriangle,
  User,
  Mail,
  Phone,
  MapPin,
  Clock,
  X as CloseIcon,
  DollarSign,
  HomeIcon,
  Heart,
  Users,
  BookOpen,
  Star,
  FileText,
  LayoutGrid,
  List,
} from "lucide-react";
import apiEndpoints from "../../apiconfig";

const profileApprovalsConfig = {
  initialState: {
    profiles: [],
    filteredProfiles: [],
    searchTerm: "",
    priorityFilter: "all",
    statusFilter: "all",
    locationFilter: "all",
    selectedProfile: null,
    loading: true,
    error: null,
    detailModalOpen: false,
    profileDetails: null,
    isRejectDialogOpen: false,
    viewMode: "grid",
    snackbar: {
      open: false,
      message: "",
      severity: "success",
    },
  },
};

// Modal style
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
  maxWidth: 1000,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  overflow: "auto",
  // Hide scrollbars in all browsers
  scrollbarWidth: "none", // Firefox
  msOverflowStyle: "none", // IE and Edge
  "&::-webkit-scrollbar": {
    display: "none", // Chrome, Safari, Opera
  },
};

export default function ProfileApprovalsPage() {
  const [state, setState] = useState(profileApprovalsConfig.initialState);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  useEffect(() => {
    fetchPendingProfiles();
  }, []);

  const fetchPendingProfiles = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const response = await fetch(apiEndpoints.approveUser);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform API data to match our component structure
      const transformedProfiles = data.map((profile) => ({
        id: profile.id,
        name: profile.name,
        // avatar: profile.profile_photo ? JSON.parse(profile.profile_photo)[0] : "",
        age: profile.age,
        location: `${profile.city || ""}, ${profile.state || ""}`,
        profession: profile.profession,
        education: profile.education,
        submittedAt: profile.createdOn,
        profileCompletion: calculateProfileCompletion(profile),
        verificationStatus: getVerificationStatus(profile),
        priority: determinePriority(profile),
        // Add more fields for detailed view
        email: profile.email,
        phone: profile.phone,
        gender: profile.gender,
        about_me: profile.about_me,
        address: profile.address,
        country: profile.country,
        city: profile.city,
        state: profile.state,
        caste: profile.caste,
        marital_status: profile.marital_status,
        religion: profile.religion,
        annual_income: profile.annual_income,
        family_type: profile.family_type,
        father_name: profile.father_name,
        mother_name: profile.mother_name,
        father_occupation: profile.father_occupation,
        mother_occupation: profile.mother_occupation,
        no_of_brothers: profile.no_of_brothers,
        no_of_sisters: profile.no_of_sisters,
        about_family: profile.about_family,
        zodiac_sign: profile.zodiac_sign,
        zodiac_star: profile.zodiac_star,
        horoscope: profile.horoscope,
        ID_proof: profile.ID_proof,
        isVerified: profile.isVerified,
        memberId: profile.user_id,
        points: profile.req_counter,
        // Add profile images
        // profile_images: profile.profile_photo ? JSON.parse(profile.profile_photo) : [],
      }));

      setState((prev) => ({
        ...prev,
        profiles: transformedProfiles,
        filteredProfiles: transformedProfiles,
        loading: false,
      }));
    } catch (error) {
      console.error("Error fetching profiles:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to load profiles. Please try again later.",
        loading: false,
        snackbar: {
          open: true,
          message: "Failed to load profiles. Please try again later.",
          severity: "error",
        },
      }));
    }
  };

  const calculateProfileCompletion = (profile) => {
    // Define fields with their respective weights
    const fieldWeights = {
      // Basic info (20%)
      name: 5,
      email: 5,
      phone: 5,
      age: 3,
      dob: 3,
      gender: 3,
      about_me: 3,
      profile_photo: 3,

      // Personal details (25%)
      birth_place: 3,
      birth_time: 2,
      education: 4,
      profession: 4,
      annual_income: 4,
      marital_status: 4,
      religion: 4,

      // Location & background (20%)
      address: 5,
      country: 4,
      state: 4,
      city: 4,
      caste: 3,

      // Family information (15%)
      family_type: 3,
      father_name: 2,
      mother_name: 2,
      father_occupation: 2,
      mother_occupation: 2,
      no_of_brothers: 2,
      no_of_sisters: 2,
      about_family: 2,

      // Astrological details (5%)
      zodiac_sign: 2,
      zodiac_star: 2,
      horoscope: 1,

      // Verification (5%)
      ID_proof: 3,
      isVerified: 2, // special check: should be === 1
    };

    let totalScore = 0;
    let maxScore = Object.values(fieldWeights).reduce((a, b) => a + b, 0);

    for (const [field, weight] of Object.entries(fieldWeights)) {
      if (field === "isVerified") {
        if (profile[field] === 1 || profile[field] === true) {
          totalScore += weight;
        }
      } else if (
        profile[field] !== null &&
        profile[field] !== undefined &&
        profile[field] !== ""
      ) {
        totalScore += weight;
      }
    }

    return Math.round((totalScore / maxScore) * 100);
  };

  const getVerificationStatus = (profile) => {
    // Determine verification status based on profile data
    if (!profile.profile_photo) return "photo_verification_pending";
    if (!profile.ID_proof) return "documents_pending";
    return "complete";
  };

  const determinePriority = (profile) => {
    // Determine priority based on profile completeness and other factors
    const completion = calculateProfileCompletion(profile);
    if (completion >= 90) return "high";
    if (completion >= 70) return "medium";
    return "low";
  };

  const handleApprove = async (profileId) => {
    try {
      const response = await fetch(apiEndpoints.approveUser, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: profileId, isActive: 1 }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove the approved profile from the list
      setState((prev) => ({
        ...prev,
        profiles: prev.profiles.filter((profile) => profile.id !== profileId),
        filteredProfiles: prev.filteredProfiles.filter(
          (profile) => profile.id !== profileId,
        ),
        detailModalOpen: false,
        snackbar: {
          open: true,
          message: "Profile approved successfully!",
          severity: "success",
        },
      }));
    } catch (error) {
      console.error("Error approving user:", error);
      setState((prev) => ({
        ...prev,
        snackbar: {
          open: true,
          message: "Failed to approve profile. Please try again.",
          severity: "error",
        },
      }));
    }
  };

  const handleViewDetails = (profile) => {
    setState((prev) => ({
      ...prev,
      profileDetails: profile,
      detailModalOpen: true,
    }));
  };

  const handleCloseModal = () => {
    setState((prev) => ({
      ...prev,
      detailModalOpen: false,
      profileDetails: null,
    }));
  };

  const handleCloseSnackbar = () => {
    setState((prev) => ({
      ...prev,
      snackbar: {
        ...prev.snackbar,
        open: false,
      },
    }));
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  // Filter profiles based on search and filter criteria
  useEffect(() => {
    let filtered = state.profiles;

    if (state.searchTerm) {
      const search = state.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (profile) =>
          profile.name.toLowerCase().includes(search) ||
          profile.id.toString().toLowerCase().includes(search), // search by user_id
      );
    }

    setState((prev) => ({ ...prev, filteredProfiles: filtered }));
  }, [state.profiles, state.searchTerm]);

  if (state.loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography>Loading profiles...</Typography>
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography color="error">{state.error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: "100%", pb: 4 }}
      display="flex"
      flexDirection="column"
      gap={4}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={700}
            gutterBottom
            sx={{ fontFamily: "Montserrat, sans-serif" }}
          >
            Profile Approvals
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "Montserrat, sans-serif" }}
          >
            Review and approve pending user profiles
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack
            direction="row"
            spacing={1}
            sx={{ bgcolor: "rgba(0,0,0,0.05)", p: 0.5, borderRadius: 2 }}
          >
            <IconButton
              size="small"
              onClick={() =>
                setState((prev) => ({ ...prev, viewMode: "table" }))
              }
              sx={{
                bgcolor: state.viewMode === "table" ? "white" : "transparent",
                boxShadow: state.viewMode === "table" ? 1 : 0,
                color: "black",
              }}
            >
              <List size={18} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() =>
                setState((prev) => ({ ...prev, viewMode: "grid" }))
              }
              sx={{
                bgcolor: state.viewMode === "grid" ? "white" : "transparent",
                boxShadow: state.viewMode === "grid" ? 1 : 0,
                color: "black",
              }}
            >
              <LayoutGrid size={18} />
            </IconButton>
          </Stack>
          <Chip
            label={`${state.filteredProfiles.length} Pending`}
            size="small"
            sx={{ bgcolor: "#fef9c3", color: "#854d0e", fontWeight: 500 }}
          />
        </Stack>
      </Box>

      {/* Search & Filters */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} width={"100%"}>
              <TextField
                placeholder="Search by Name or User ID..."
                size="small"
                fullWidth
                value={state.searchTerm}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, searchTerm: e.target.value }))
                }
                InputProps={{
                  startAdornment: (
                    <Search
                      size={16}
                      style={{ marginRight: 6, color: "#9ca3af" }}
                    />
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Profiles grid */}
      {/* Profiles View */}
      {state.viewMode === "table" ? (
        <Card sx={{ borderRadius: 3, overflow: "auto" }}>
          <TableContainer>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 700,
                    }}
                  >
                    Profile
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 700,
                    }}
                  >
                    Details
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 700,
                    }}
                  >
                    Completion
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 700,
                    }}
                  >
                    Submitted
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 700,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.filteredProfiles.map((profile) => (
                  <TableRow key={profile.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar src={profile.avatar}>{profile.name[0]}</Avatar>
                        <Box>
                          <Typography
                            fontWeight={600}
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                          >
                            {profile.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                          >
                            ID: {profile.memberId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                          {profile.age} yrs • {profile.location}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Briefcase size={14} color="#666" />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                          >
                            {profile.profession}
                          </Typography>
                        </Box>
                        {profile.points > 0 && (
                          <Chip
                            label={`${profile.points} Pts`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              alignSelf: "flex-start",
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={profile.profileCompletion}
                          sx={{ height: 5, borderRadius: 2, mb: 0.5 }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                          {profile.profileCompletion}% Complete
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        {formatDate(profile.submittedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Eye size={14} />}
                          onClick={() => handleViewDetails(profile)}
                          sx={{
                            textTransform: "none",
                            color: "black",
                            borderColor: "#ffe599",
                            fontFamily: "Montserrat, sans-serif",
                          }}
                        >
                          View
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Check size={14} />}
                          onClick={() => handleApprove(profile.id)}
                          sx={{
                            textTransform: "none",
                            bgcolor: "#ffe599",
                            color: "black",
                            fontFamily: "Montserrat, sans-serif",
                          }}
                        >
                          Approve
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {state.filteredProfiles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        No profiles found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        <Grid container spacing={{xs:5,md:4}}>
          {state.filteredProfiles.map((profile) => (
            <Grid
              item
              key={profile.id}
              sx={{
                display: "flex",
                width: {xs:"100%",sm:"45%", md:"48%"},
                // flexGrow: 1,
                minWidth: 300,
              }}
              // 👈 make cards stretch
            >
              <Card
                sx={{
                  borderRadius: 3,
                  p: 2,
                  flex: 1, // 👈 makes all cards equal width
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <CardHeader
                  avatar={
                    <Avatar src={profile.avatar}>{profile.name[0]}</Avatar>
                  }
                  title={
                    <Typography
                      fontWeight="600"
                      noWrap
                      sx={{
                        fontFamily: "Montserrat, sans-serif",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {profile.name}
                    </Typography>
                  }
                  subheader={`${profile.memberId} • ${profile.age} years • ${
                    profile.location.split(",")[0]
                  }`}
                  sx={{ pb: 1 }}
                />

                <CardContent sx={{ pt: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={0.5}
                    noWrap
                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    <Briefcase size={14} style={{ marginRight: 6 }} />
                    {profile.profession}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={0.5}
                    noWrap
                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    <GraduationCap size={14} style={{ marginRight: 6 }} />
                    {profile.education}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={1}
                    noWrap
                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    <Calendar size={14} style={{ marginRight: 6 }} />
                    Submitted {formatDate(profile.submittedAt)}
                  </Typography>

                  <Box mb={1}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      Profile Completion
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={profile.profileCompletion}
                      sx={{ height: 5, borderRadius: 2, mt: 0.5 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      {profile.profileCompletion}%
                    </Typography>
                  </Box>

                  <Box display="flex" gap={1.5} mt={2} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Eye size={16} />}
                      sx={{
                        textTransform: "none",
                        color: "#000000ff",
                        borderColor: "#e0e0e0",
                        fontFamily: "Montserrat, sans-serif",
                        "&:hover": {
                          borderColor: "#e0e0e0",
                          backgroundColor: "transparent",
                          color: "#000000ff",
                        },
                      }}
                      onClick={() => handleViewDetails(profile)}
                    >
                      View Details
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<Check size={16} />}
                      sx={{
                        textTransform: "none",
                        bgcolor: "#ffe599",
                        color: "black",
                        fontFamily: "Montserrat, sans-serif",
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: "#ffe599",
                          boxShadow: "none",
                        },
                      }}
                      onClick={() => handleApprove(profile.id)}
                    >
                      Approve
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {state.filteredProfiles.length === 0 && !state.loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 4,
            fontFamily: "Montserrat, sans-serif",
          }}
        >
          <Typography>No profiles found matching your criteria.</Typography>
        </Box>
      )}

      <Dialog
        open={state.detailModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        {state.profileDetails && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={
                    state.profileDetails.name
                      ? state.profileDetails.name[0]
                      : ""
                  }
                  alt={state.profileDetails.name}
                  sx={{ width: 60, height: 60 }}
                />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {state.profileDetails.name}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      ID: {state.profileDetails.memberId}
                    </Typography>
                    {state.profileDetails.points > 0 && (
                      <Chip
                        label={`${state.profileDetails.points} Pts`}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: "0.7rem",
                          bgcolor: "#f5f5f5",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent dividers sx={{ p: 3 }}>
              {[
                {
                  title: "System Information",
                  fields: [
                    ["Member ID", "memberId"],
                    ["Points", "points"],
                    ["Submitted On", "submittedAt"],
                  ],
                },
                {
                  title: "Personal Information",
                  fields: [
                    ["Name", "name"],
                    ["Email", "email"],
                    ["Phone", "phone"],
                    ["Age", "age"],
                    ["Gender", "gender"],
                    ["Education", "education"],
                    ["Profession", "profession"],
                    ["Annual Income", "annual_income"],
                    ["Marital Status", "marital_status"],
                    ["Religion", "religion"],
                    ["Caste", "caste"],
                    ["Location", "location"],
                    ["Address", "address"],
                    ["About Me", "about_me"],
                  ],
                },
                {
                  title: "Family Information",
                  fields: [
                    ["Family Type", "family_type"],
                    ["Father Name", "father_name"],
                    ["Mother Name", "mother_name"],
                    ["Father Occupation", "father_occupation"],
                    ["Mother Occupation", "mother_occupation"],
                    ["Brothers", "no_of_brothers"],
                    ["Sisters", "no_of_sisters"],
                    ["About Family", "about_family"],
                  ],
                },
              ].map((section) => (
                <Box key={section.title} mb={3}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    fontWeight={600}
                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    {section.title}
                  </Typography>
                  <Grid container spacing={2}>
                    {section.fields.map(([label, key]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                          <strong style={{ color: "#555" }}>{label}:</strong>{" "}
                          {key === "submittedAt"
                            ? formatDate(state.profileDetails[key])
                            : state.profileDetails[key] || "N/A"}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                  <Divider sx={{ mt: 3 }} />
                </Box>
              ))}

              {/* Uploads Section */}
              <Box mb={2}>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight={600}
                  sx={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Uploads
                </Typography>
                <Grid container spacing={2}>
                  {[
                    ["Profile Photo", "profile_photo"],
                    ["Horoscope", "horoscope"],
                    ["ID Proof", "ID_proof"],
                  ].map(([label, key]) => (
                    <Grid item xs={12} sm={4} key={key}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        gutterBottom
                        sx={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        {label}
                      </Typography>
                      {state.profileDetails[key] ? (
                        <Box
                          component="img"
                          src={`${apiEndpoints.image}${state.profileDetails[key]}`}
                          alt={label}
                          sx={{
                            width: "100%",
                            height: 150,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: "1px solid #eee",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            window.open(
                              `${apiEndpoints.image}${state.profileDetails[key]}`,
                              "_blank",
                            )
                          }
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: 100,
                            bgcolor: "#f9f9f9",
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px dashed #ccc",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            N/A
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, borderTop: "1px solid #eee" }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<X size={16} />}
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    isRejectDialogOpen: true,
                    selectedProfile: state.profileDetails,
                    detailModalOpen: false,
                  }))
                }
                sx={{ textTransform: "none" }}
              >
                Reject
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={handleCloseModal}
                sx={{ color: "text.secondary", textTransform: "none" }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<Check size={16} />}
                onClick={() => handleApprove(state.profileDetails.id)}
                sx={{
                  bgcolor: "#ffe599",
                  color: "black",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#ffd54f" },
                }}
              >
                Approve
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={state.snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={state.snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {state.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
